import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import ArticleHeader from './components/ArticleHeader';
import ArticleBody from './components/ArticleBody';
import ArticleToc from './components/ArticleToc';
import ReadingProgress from './components/ReadingProgress';
import RelatedArticles from './components/RelatedArticles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);
  const headings = article ? article.sections.map((s) => s.heading) : [];

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = articles.filter((a) => a.id !== article.id && a.categoryId === article.categoryId);
    const others = articles.filter((a) => a.id !== article.id && a.categoryId !== article.categoryId);
    return [...sameCat, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: 'zh-CN',
          articleSection: article.categoryName,
          keywords: article.tags.join(','),
          author: { '@type': 'Person', name: article.author },
          publisher: {
            '@type': 'Organization',
            name: '爆料看料 · 深扒观察室',
          },
          mainEntityOfPage: `https://cicccqh.cn/article/${article.id}`,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cicccqh.cn/' },
            {
              '@type': 'ListItem',
              position: 2,
              name: '专题分类',
              item: 'https://cicccqh.cn/topics',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.categoryName,
              item: `https://cicccqh.cn/topics?cat=${article.categoryId}`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: article.title,
              item: `https://cicccqh.cn/article/${article.id}`,
            },
          ],
        },
      ],
    };
  }, [article]);

  useSeo({
    title: article
      ? `${article.title} - 爆料看料深度拆解`
      : '内容不存在 - 爆料看料',
    description: article
      ? article.excerpt
      : '抱歉，你想查看的深度拆解不存在或已被移除，可返回专题分类继续浏览。',
    keywords: article ? `${article.tags.join(',')},爆料看料,深度拆解` : '爆料看料',
    path: article ? `/article/${article.id}` : '/article',
    jsonLd,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-700">
            <i className="ri-file-unknow-line" />
          </span>
          <h2 className="font-heading mt-6 text-2xl font-bold text-foreground-950">这篇拆解走丢了</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            你访问的内容可能已被移除或链接有误。不妨回到专题分类，或看看我们的热榜推荐。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回专题分类
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <ReadingProgress />
      <ArticleHeader article={article} />
      <div className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 py-10 lg:grid-cols-[minmax(0,1fr)_260px] lg:py-14">
            <div>
              <ArticleBody article={article} />
            </div>
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                <ArticleToc headings={headings} />
                <div className="rounded-lg bg-accent-900 p-5">
                  <h3 className="font-heading text-sm font-bold text-background-50">线索征集</h3>
                  <p className="mt-2 text-xs leading-relaxed text-accent-100">
                    手里有公开信息支撑的线索？告诉我们，编辑部会评估是否纳入深扒计划。
                  </p>
                  <a
                    href="#subscribe"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-secondary-200 hover:text-secondary-100"
                  >
                    提交线索
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <RelatedArticles articles={related} />
    </SiteLayout>
  );
}